import React, { useMemo } from 'react';
import { Alert, Space, Typography, theme } from 'antd';
import { Link } from 'react-router-dom';
import {
  InfoCircleOutlined,
  WarningOutlined,
  SyncOutlined,
  CalendarOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';

/**
 * Minimal billing reminders for the dashboard (Ant Design tokens only — no hardcoded colors).
 * Data shape: auth.user.extra.privileges.license_status from UserLoginResource.
 */
const { Text } = Typography;

const TRIAL_URGENT_DAYS = 14;
const PAID_RENEWAL_NOTICE_DAYS = 15;

function safeRemainingDays(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

function formatDate(isoOrDate) {
  if (!isoOrDate) return '';
  try {
    const d = new Date(isoOrDate);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

const licensesLink = (
  <Link to="/licenses">Billing &amp; licenses</Link>
);

function LicenseReminders({ license_status }) {
  const { token } = theme.useToken();

  const items = useMemo(() => {
    const list = [];
    if (!license_status || license_status.status === 'none') {
      return list;
    }

    const type = license_status.type;
    const status = license_status.status;
    const remaining = safeRemainingDays(license_status.remaining_days);
    const expiryDate = formatDate(license_status.expiry_date);
    const accessEnds = formatDate(license_status.access_ends_at);
    const planName = license_status.plan_name;

    if (type === 'paid') {
      if (status === 'past_due') {
        list.push({
          key: 'past_due',
          type: 'warning',
          className: '',
          icon: <CreditCardOutlined />,
          title: 'Payment issue',
          description: (
            <>
              We could not charge your saved payment method. Update your card to avoid interruption.{' '}
              {licensesLink}.
            </>
          ),
        });
      }

      if (license_status.in_grace_period && license_status.grace_remaining_days != null) {
        const g = safeRemainingDays(license_status.grace_remaining_days);
        list.push({
          key: 'grace',
          type: 'warning',
          className: '',
          icon: <WarningOutlined />,
          title: 'Grace period',
          description: (
            <>
              Your subscription period ended; you have <strong>{g}</strong> day{g === 1 ? '' : 's'} of grace
              access remaining. {licensesLink}.
            </>
          ),
        });
      }

      if (status === 'canceled' && (remaining > 0 || accessEnds)) {
        list.push({
          key: 'canceled_access',
          type: 'warning',
          className: '',
          icon: <CalendarOutlined />,
          title: 'Subscription canceled',
          description: (
            <>
              You still have access until{' '}
              <strong>{accessEnds || expiryDate || 'the end of your billing period'}</strong>.
              Reactivate anytime from {licensesLink}.
            </>
          ),
        });
      }

      if (
        status === 'active' &&
        remaining > 0 &&
        remaining <= PAID_RENEWAL_NOTICE_DAYS &&
        !license_status.in_grace_period
      ) {
        list.push({
          key: 'renewal',
          type: '',
          className: 'ant-alert-light-gray',
          icon: <SyncOutlined />,
          title: 'Upcoming renewal',
          description: (
            <>
              {planName ? (
                <>
                  <Text strong>{planName}</Text>
                  {' · '}
                </>
              ) : null}
              Current period ends in <strong>{remaining}</strong> day{remaining === 1 ? '' : 's'}
              {expiryDate ? <> ({expiryDate})</> : null}. We will charge your default payment method on
              renewal unless you cancel before then. Manage billing in {licensesLink}.
            </>
          ),
        });
      }
    }

    if (type === 'trial') {
      if (status === 'expired') {
        list.push({
          key: 'trial_expired',
          type: 'warning',
          className: '',
          icon: <WarningOutlined />,
          title: 'Trial ended',
          description: <>Choose a plan to continue full access. {licensesLink}.</>,
        });
      } else if (remaining <= TRIAL_URGENT_DAYS) {
        list.push({
          key: 'trial_urgent',
          type: 'warning',
          className: '',
          icon: <WarningOutlined />,
          title: 'Trial ending soon',
          description: (
            <>
              {remaining === 0 ? (
                <>Your trial ends today.</>
              ) : (
                <>
                  <strong>{remaining}</strong> day{remaining === 1 ? '' : 's'} left
                  {expiryDate ? <> · {expiryDate}</> : null}.
                </>
              )}{' '}
              <Link to="/licenses">Upgrade</Link> to keep using Varexe.
            </>
          ),
        });
      } else {
        list.push({
          key: 'trial_active',
          type: 'info',
          className: '',
          icon: <InfoCircleOutlined />,
          title: 'Trial active',
          description: (
            <>
              {planName ? (
                <>
                  <Text strong>{planName}</Text>
                  {' · '}
                </>
              ) : null}
              <strong>{remaining}</strong> days remaining
              {expiryDate ? <> · ends {expiryDate}</> : null}.{' '}
              <Link to="/licenses">View plans</Link>
            </>
          ),
        });
      }
    }

    return list;
  }, [license_status]);

  if (!items.length) {
    return null;
  }

  return (
    <Space orientation="vertical" size="small" style={{ width: '100%', marginBottom: token.marginMD }}>
      {items.map((item) => (
        <Alert
          key={item.key}
          type={item.type}
          showIcon
          icon={item.icon}
          title={item.title}
          description={item.description}
          className={item.className}
          styles={{
            root: {
              borderRadius: token.borderRadiusLG,
              // borderColor: token.colorBorderSecondary,
            }
          }}
        />
      ))}
    </Space>
  );
}

export default LicenseReminders;
